import { useEffect, useRef, useState } from 'react';

import { Add01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { Button, ToggleSwitch } from 'components/FormComponents';
import { StatusPill } from 'components/Pills/StatusPills';
import { TagSearchDropdown } from 'components/TagSearchDropdown/TagSearchDropdown';

import styles from './GroupMappingConfig.module.scss';

const AssignmentField = ({
  control,
  getValues,
  name,
  label,
  options,
  addLabel,
  searchPlaceholder,
  availableLabel,
  noResultsLabel,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const triggerRef = useRef(null);

  return (
    <div className="u-margin--large">
      <h3 className="t-small u-font-weight-600 u-margin-bottom--small">{label}</h3>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => {
          const selectedIds = value || [];
          const selectedOptions = selectedIds
            .map(id => options.find(option => option.id === id))
            .filter(Boolean);

          const handleSelect = optionId => {
            const latestIds = getValues(name) || [];
            if (latestIds.includes(optionId)) {
              onChange(latestIds.filter(id => id !== optionId));
            } else {
              onChange([...latestIds, optionId]);
            }
          };

          const handleRemove = optionId => {
            const latestIds = getValues(name) || [];
            onChange(latestIds.filter(id => id !== optionId));
          };

          return (
            <div className={styles.tagBox}>
              {selectedOptions.map(option => (
                <span key={option.id} className={styles.tagChip}>
                  <span className={styles.tagDot} />
                  {option.name}
                  <span
                    className={styles.tagRemove}
                    onClick={e => {
                      e.stopPropagation();
                      handleRemove(option.id);
                    }}>
                    <HugeiconsIcon icon={Cancel01Icon} size="12" strokeWidth="2.5" />
                  </span>
                </span>
              ))}
              <div
                ref={triggerRef}
                onClick={() => setDropdownVisible(v => !v)}
                className={styles.addTagBtn}>
                <HugeiconsIcon icon={Add01Icon} size={12} strokeWidth="2.5" />
                {addLabel}
                <TagSearchDropdown
                  visible={dropdownVisible}
                  usePortal
                  tags={options}
                  selectedIds={selectedIds}
                  onSelect={handleSelect}
                  onClose={() => setDropdownVisible(false)}
                  triggerRef={triggerRef}
                  searchPlaceholder={searchPlaceholder}
                  availableLabel={availableLabel}
                  noResultsLabel={noResultsLabel}
                />
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export const GroupMappingConfig = ({
  group,
  roles = [],
  teams = [],
  onUpdate = () => {},
  onClose = () => {},
}) => {
  const { register, reset, control, handleSubmit, getValues } = useForm();

  useEffect(() => {
    reset({
      auto_provision: group.auto_provision,
      role_ids: group.role_ids || [],
      team_ids: group.team_ids || [],
    });
  }, [group.workos_group_id]);

  const onSubmit = data => {
    onUpdate(group.workos_group_id, {
      name: group.name,
      auto_provision: data.auto_provision,
      role_ids: data.role_ids || [],
      team_ids: data.team_ids || [],
    });
  };

  return (
    <form
      className={cn('card', 'card-with-border', 'card--small', 'u-width-50')}
      onSubmit={handleSubmit(onSubmit)}>
      <div>
        <div className="u-padding--large u-divider-bottom-border">
          <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
            <h2 className="title-2">{group.name}</h2>
            <StatusPill
              status={group.mapped ? 'success' : 'warning'}
              text={group.mapped ? 'Mapped' : 'Needs mapping'}
            />
          </div>
          <p className="t-small secondary-text">
            {group.member_count} member{group.member_count === 1 ? '' : 's'}
          </p>
        </div>

        {!group.mapped && (
          <div className="u-padding-x--large u-padding-top--large">
            <div className={styles.warningBanner}>
              Members of this group won't be created in Checkmate until you map it to at least one
              role.
            </div>
          </div>
        )}

        <div className="u-padding--large u-divider-bottom-border">
          <ToggleSwitch
            register={register}
            name="auto_provision"
            className="u-margin-0"
            label="Automatically provision new members"
            tooltip="New directory users added to this group get a Checkmate account instantly"
          />
        </div>

        <div>
          <AssignmentField
            control={control}
            getValues={getValues}
            name="role_ids"
            label="Roles granted"
            options={roles}
            addLabel="Add role"
            searchPlaceholder="Search roles..."
            availableLabel="Available roles"
            noResultsLabel="No roles found"
          />
          <AssignmentField
            control={control}
            getValues={getValues}
            name="team_ids"
            label="Teams assigned"
            options={teams}
            addLabel="Add team"
            searchPlaceholder="Search teams..."
            availableLabel="Available teams"
            noResultsLabel="No teams found"
          />
        </div>

        {group.members?.length > 0 && (
          <div className="u-padding--large u-divider-top-border">
            <div
              className={cn(
                'u-flex-box',
                'u-flex-justify-between',
                'u-flex-align-center',
                'u-margin-bottom--small',
              )}>
              <h3 className="t-small u-font-weight-600">Members in this group</h3>
              <span className="t-small secondary-text">
                Showing {group.members.length} of {group.member_count}
              </span>
            </div>
            <div className={styles.assignedRows}>
              {group.members.map(member => (
                <div
                  key={member.workos_user_id}
                  className={cn('card', 'card-with-border', styles.memberRow)}>
                  <div>
                    <div className="t-body u-font-weight-600">{member.name}</div>
                    <div className="t-small secondary-text">{member.email}</div>
                  </div>
                  <StatusPill
                    status={member.state === 'active' ? 'success' : 'neutral'}
                    text={member.state}
                    capitalize
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-between')}>
        <Button onClick={onClose} type="secondary">
          Cancel
        </Button>
        <Button submit>Save</Button>
      </div>
    </form>
  );
};
